import PublicProfile from "./pages/PublicProfile";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase, authStorageKey } from "./supabase";

import Navbar from "./components/Navbar";
import OfflineBanner from "./components/OfflineBanner";
import OnboardingModal from "./components/OnboardingModal";
import CookieConsent from "./components/CookieConsent";
import DialogHost from "./components/DialogHost";
import UpdateBanner from "./components/UpdateBanner";
import InstallBanner from "./components/InstallBanner";
import Home from "./pages/Home";
import Upload from "./pages/Upload";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Trending from "./pages/Trending";
import Saved from "./pages/Saved";
import Search from "./pages/Search";
import DesignView from "./pages/DesignView";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import HashtagPage from "./pages/HashtagPage";
import Help from "./pages/Help";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";
import Footer from "./components/Footer";
import About from "./components/About";

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Defaults assume maintenance is off and the visitor isn't an admin —
  // both checks are async, and blocking the whole app's first render on
  // them would add a network round trip to every single page load just to
  // cover the rare case where maintenance mode is actually on. If it is on,
  // there's a brief flash of the normal app (or of the maintenance page for
  // an admin) before this resolves, which is an acceptable tradeoff for not
  // slowing down the common case.
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "" });
  const [isAdminUser, setIsAdminUser] = useState(false);

  // Verifying the signup OTP hands back a real, authenticated session before
  // the wizard's later steps (username/gender/password) ever run — without
  // this, reloading or tapping any nav link right after that point leaves
  // someone wandering the whole app "signed in" with no username and no
  // password ever set, and no way to sign back in later if that session is
  // ever lost. Gate everything but the pages they still need behind having
  // an actual `users` row; Login itself resumes the wizard from here.
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  // On Android the Chrome tab and the installed app are one browser: they share
  // one login. Signing in as someone else (or out) in one used to leave the other
  // window still showing — and acting as — the previous account until it was
  // closed and reopened. The "storage" event fires only in the OTHER windows, so
  // this reloads a window when the login changes elsewhere, never on its own
  // sign-in or on the routine token refreshes (same person, new token).
  useEffect(() => {
    function loggedInId(raw) {
      try {
        return raw ? JSON.parse(raw)?.user?.id ?? null : null;
      } catch {
        return null;
      }
    }

    function onStorage(e) {
      // key === null: all site storage was cleared.
      if (e.key !== null && e.key !== authStorageKey) return;
      if (e.key === null || loggedInId(e.oldValue) !== loggedInId(e.newValue)) {
        window.location.reload();
      }
    }

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A referral link is `gatheryou.in/<any page>?ref=<username>` — captured
  // here at the app root (not just on Login) since the actual link people
  // share points at /install (the PWA-install prompt) or a specific design,
  // not always straight at /login. Saved to sessionStorage so it survives
  // both a later navigation to /login and the Google OAuth round trip (same
  // tab, so sessionStorage persists across the redirect to accounts.google.com
  // and back). Read once at signup completion in Login.jsx.
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (!ref) return;
    try {
      sessionStorage.setItem("gatheryou_referred_by", ref);
    } catch {
      // private mode / blocked storage — referral just won't be credited
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    // Finishing signup creates the users row without any auth event firing,
    // so nothing here would ever notice it — Login announces it instead. The
    // flag also stops a check that started just before the row existed (the
    // password update fires one) from landing late and flipping it back.
    let profileJustCreated = false;

    async function checkProfileComplete(userId) {
      if (!userId) {
        profileJustCreated = false;
        if (!cancelled) setProfileIncomplete(false);
        return;
      }

      const { data, error } = await supabase
        .from("users")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.log("PROFILE COMPLETENESS CHECK ERROR:", error);
        return;
      }
      setProfileIncomplete(!data && !profileJustCreated);
      // Consumed after the one check it's meant to bridge — left permanently
      // true, it would keep suppressing this check for the rest of the tab's
      // life, even if the row genuinely disappears later (e.g. a moderation
      // delete while the session token is still valid).
      profileJustCreated = false;
    }

    function onProfileCreated() {
      profileJustCreated = true;
      setProfileIncomplete(false);
    }
    window.addEventListener("gatheryou:profile-created", onProfileCreated);

    // supabase-js runs this callback while it holds its internal auth lock —
    // awaiting any other supabase call from inside it (getSession, a query)
    // deadlocks until that lock times out (~5s), stalling every other auth
    // call in the meantime (sign-in, OTP, password update all timing out).
    // Deferred to a timeout so the callback itself returns immediately; the
    // initial session is delivered through this same callback, so no
    // separate first check is needed.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => checkProfileComplete(session?.user?.id), 0);
    });

    return () => {
      cancelled = true;
      window.removeEventListener("gatheryou:profile-created", onProfileCreated);
      sub?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    async function checkAdmin() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase.rpc("am_i_admin");
      if (error) {
        console.log("ADMIN CHECK ERROR:", error);
        return;
      }
      setIsAdminUser(!!data);
    }

    supabase
      .from("site_settings")
      .select("value")
      .eq("key", "maintenance_mode")
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.log("LOAD MAINTENANCE MODE ERROR:", error);
          return;
        }
        if (data?.value) {
          setMaintenance(data.value);
          // isAdminUser only ever matters for the maintenance gate below —
          // no need to spend an RPC call on every single visitor's every
          // page load finding that out when maintenance is off, which is
          // true the overwhelming majority of the time.
          if (data.value.enabled) checkAdmin();
        }
      });
  }, []);

  // "/" jumps to Search — skipped while typing in any field, so it never
  // hijacks normal text entry (captions, comments, form fields, etc.).
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== "/") return;

      const tag = document.activeElement?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (isTyping) return;

      e.preventDefault();
      if (location.pathname !== "/search") navigate("/search");
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate, location.pathname]);

  // /login stays reachable so an admin (or anyone) can still sign in during
  // maintenance; /admin stays reachable so the admin can turn it back off —
  // a non-admin landing there just sees Admin's own "no access" message,
  // same as always.
  if (maintenance.enabled && !isAdminUser && location.pathname !== "/login" && location.pathname !== "/admin") {
    return <MaintenancePage message={maintenance.message} />;
  }

  // A handful of pages stay reachable even with an unfinished signup —
  // /login so Login's own effect can resume the wizard, the rest because
  // there's no account-specific content on them to protect.
  if (
    profileIncomplete &&
    !["/login", "/terms", "/privacy", "/help", "/install"].includes(location.pathname)
  ) {
    return <Login />;
  }

  return (
    <>
      <UpdateBanner />
      <OfflineBanner />
      <InstallBanner />
      {/* Showing this over the login/signup form itself is premature — it
          walks through features (posting, following, Trending) a brand-new
          visitor can't reach yet, and blocks the form until dismissed. */}
      {location.pathname !== "/login" && location.pathname !== "/install" && <OnboardingModal />}
      <Navbar />
      <CookieConsent />
      <DialogHost />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/login" element={<Login />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/trending" element={<Trending />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/search" element={<Search />} />
       <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/d/:designId" element={<DesignView />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/about" element={<About />} />
        <Route path="/tag/:tag" element={<HashtagPage />} />
        <Route path="/help" element={<Help />} />
        <Route path="/install" element={<Install />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
            <Footer />
    </>
  );
}

function MaintenancePage({ message }) {
  return (
    <div style={maintenancePage}>
      <p style={maintenanceHeading}>We'll be back soon</p>
      <p style={maintenanceText}>
        {message || "GatherYou is down for maintenance. Please check back shortly."}
      </p>
    </div>
  );
}

const maintenancePage = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  textAlign: "center",
  background: "var(--bg-page, #fff)"
};

const maintenanceHeading = {
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading, #222)",
  margin: "0 0 8px"
};

const maintenanceText = {
  fontSize: 14,
  color: "var(--text-muted, #777)",
  margin: 0,
  maxWidth: 320
};

export default App;
